import { useState } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import {
  useCreateCriterion,
  useCriteria,
  useDeleteCriterion,
  useUpdateCriterion,
  type CriteriaScope,
} from '@/hooks/useEndpointTestEvals';
import { useToast } from '@/contexts/ToastContext';
import { useFlag } from '@/hooks/useFeatureFlags';
import type {
  CheckDefinition,
  CheckType,
  CreateEndpointTestCriterionInput,
  EndpointTestCriterionKind,
  EndpointTestCriterionSource,
  GradedDefinition,
} from '@/types/endpointTests';

/** The four checks, as the picker names them. */
export const CHECK_TYPES: Array<{ value: CheckType; label: string; needsPath: boolean; needsValue: boolean }> = [
  { value: 'schema', label: 'Output matches schema', needsPath: false, needsValue: false },
  { value: 'field-present', label: 'Field is present', needsPath: true, needsValue: false },
  { value: 'field-equals', label: 'Field equals', needsPath: true, needsValue: true },
  { value: 'field-contains', label: 'Field contains', needsPath: true, needsValue: true },
];

const isCheck = (definition: CheckDefinition | GradedDefinition): definition is CheckDefinition =>
  'type' in definition;

/** The pass bar a graded criterion uses when its definition sets none; the service's default (task 1265). */
export const GRADED_PASS_THRESHOLD_DEFAULT = 0.7;

/** A graded definition's pass bar: its `passThreshold` when it is a number in [0, 1], else the default. */
export function passThresholdOf(definition: unknown): number {
  const value = (definition as { passThreshold?: unknown } | null | undefined)?.passThreshold;
  return typeof value === 'number' && value >= 0 && value <= 1 ? value : GRADED_PASS_THRESHOLD_DEFAULT;
}

/** One line describing a criterion's definition. */
export function describeDefinition(criterion: CreateEndpointTestCriterionInput): string {
  const { definition } = criterion;
  if (isCheck(definition)) {
    const check = CHECK_TYPES.find((type) => type.value === definition.type);
    return [check?.label ?? definition.type, definition.path, definition.value ? `"${definition.value}"` : null]
      .filter(Boolean)
      .join(' · ');
  }
  return definition.rubric;
}

const inputClass =
  'w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-xs text-gray-900 dark:text-white';

interface CriterionFormProps {
  initial?: CreateEndpointTestCriterionInput;
  /** The service cannot change a saved criterion's kind, so editing locks it. */
  kindLocked?: boolean;
  onSave: (criterion: CreateEndpointTestCriterionInput) => void | Promise<void>;
  onCancel: () => void;
  saving?: boolean;
}

/**
 * The row form: name, rubric, required; then kind (graded or check, with the
 * check's path and value) and a graded criterion's pass bar, always shown
 * inline (tasks 1186, 1265, 1292). A new criterion defaults to graded, required, bar 0.7;
 * the default bar is not written to the definition, so the service's default
 * applies.
 */
export function CriterionForm({ initial, kindLocked, onSave, onCancel, saving }: CriterionFormProps) {
  const initialCheck = initial && isCheck(initial.definition) ? initial.definition : undefined;
  const initialGraded = initial && !isCheck(initial.definition) ? initial.definition : undefined;
  const [name, setName] = useState(initial?.name ?? '');
  const [kind, setKind] = useState<EndpointTestCriterionKind>(initial?.kind ?? 'graded');
  const [checkType, setCheckType] = useState<CheckType>(initialCheck?.type ?? 'field-present');
  const [path, setPath] = useState(initialCheck?.path ?? '');
  const [value, setValue] = useState(initialCheck?.value ?? '');
  const [rubric, setRubric] = useState(initialGraded?.rubric ?? '');
  const [passBar, setPassBar] = useState(String(passThresholdOf(initialGraded)));
  const [required, setRequired] = useState(initial?.required ?? true);

  const check = CHECK_TYPES.find((type) => type.value === checkType)!;
  const passThreshold = passBar.trim() === '' ? Number.NaN : Number(passBar);
  const passBarValid = Number.isFinite(passThreshold) && passThreshold >= 0 && passThreshold <= 1;
  const valid =
    name.trim() !== '' &&
    (kind === 'graded'
      ? rubric.trim() !== '' && passBarValid
      : (!check.needsPath || path.trim() !== '') && (!check.needsValue || value.trim() !== ''));

  const submit = () => {
    if (!valid) return;
    const definition: CheckDefinition | GradedDefinition =
      kind === 'check'
        ? {
            type: checkType,
            ...(check.needsPath ? { path: path.trim() } : {}),
            ...(check.needsValue ? { value: value.trim() } : {}),
          }
        : {
            rubric: rubric.trim(),
            ...(passThreshold !== GRADED_PASS_THRESHOLD_DEFAULT ? { passThreshold } : {}),
          };
    void onSave({ name: name.trim(), kind, definition, required });
  };

  const labelClass = 'block text-[11px] font-medium text-gray-600 dark:text-gray-300 mb-0.5';

  return (
    <div data-testid="criterion-form" className="space-y-2 rounded-md border border-indigo-200 dark:border-indigo-900 p-2.5 bg-indigo-50/40 dark:bg-indigo-950/30">
      <div>
        <span className={labelClass}>Name</span>
        <input aria-label="Criterion name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. cites only real documentation" className={inputClass} />
      </div>
      {kind === 'graded' && (
        <div>
          <span className={labelClass}>Rubric</span>
          <textarea aria-label="Rubric" value={rubric} onChange={(e) => setRubric(e.target.value)} rows={2} placeholder="Describe what a good answer looks like." className={inputClass} />
        </div>
      )}
      <label className="inline-flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
        <input type="checkbox" aria-label="Required" checked={required} onChange={(e) => setRequired(e.target.checked)} className="h-3.5 w-3.5 rounded border-gray-300" />
        Required <span className="text-gray-400">(off = informational: scored, never gating)</span>
      </label>
      {/* Kind, the check fields and the pass bar (tasks 1186, 1265): always
          shown inline below Required (task 1292). */}
      <div className="space-y-2 text-xs">
        <select aria-label="Kind" value={kind} disabled={kindLocked} title={kindLocked ? 'A saved criterion keeps its kind' : undefined} onChange={(e) => setKind(e.target.value as EndpointTestCriterionKind)} className={`${inputClass} disabled:opacity-60`}>
          <option value="graded">graded</option>
          <option value="check">check</option>
        </select>
        {kind === 'check' ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <select aria-label="Check" value={checkType} onChange={(e) => setCheckType(e.target.value as CheckType)} className={inputClass}>
              {CHECK_TYPES.map((type) => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
            {check.needsPath && (
              <input aria-label="Path" value={path} onChange={(e) => setPath(e.target.value)} placeholder="severity" className={`${inputClass} font-mono`} />
            )}
            {check.needsValue && (
              <input aria-label="Value" value={value} onChange={(e) => setValue(e.target.value)} placeholder="billing" className={inputClass} />
            )}
          </div>
        ) : (
          <div>
            <label className="inline-flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
              Pass bar
              <input
                type="number"
                aria-label="Pass bar"
                min={0}
                max={1}
                step={0.05}
                value={passBar}
                onChange={(e) => setPassBar(e.target.value)}
                aria-invalid={!passBarValid}
                className={`${inputClass} w-20`}
              />
            </label>
            <p className={`mt-0.5 text-[11px] ${passBarValid ? 'text-gray-500 dark:text-gray-400' : 'text-red-600 dark:text-red-400'}`}>
              A result passes this criterion at or above this score.
            </p>
          </div>
        )}
      </div>
      <div className="flex justify-end gap-1.5">
        <button type="button" onClick={onCancel} className="px-2 py-1 text-xs text-gray-600 dark:text-gray-300">Cancel</button>
        <button type="button" onClick={submit} disabled={!valid || saving} className="px-2.5 py-1 text-xs font-medium text-white bg-indigo-600 rounded-md disabled:opacity-50">
          Save criterion
        </button>
      </div>
    </div>
  );
}

/** A criterion's read view: name, one-line definition, kind, required chip, "suggested" on generated ones. */
export function CriterionRow({
  testId,
  criterion,
  onEdit,
  onRemove,
}: {
  testId: string;
  criterion: CreateEndpointTestCriterionInput & { source?: EndpointTestCriterionSource };
  onEdit: () => void;
  onRemove: () => void;
}) {
  return (
    <li data-testid={testId} className="flex items-start gap-2 py-1.5 text-xs">
      <div className="flex-1 min-w-0">
        <p className="text-gray-900 dark:text-white font-medium">{criterion.name}</p>
        <p className="text-gray-500 dark:text-gray-400 truncate">{describeDefinition(criterion)}</p>
      </div>
      <span className="flex items-center gap-1.5 shrink-0">
        <span className="text-gray-500 dark:text-gray-400">
          {criterion.kind === 'graded' ? `graded · bar ${passThresholdOf(criterion.definition)}` : criterion.kind}
        </span>
        <span
          className={`px-1.5 py-0.5 rounded-full font-medium ${
            criterion.required
              ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200'
              : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
          }`}
        >
          {criterion.required ? 'required' : 'informational'}
        </span>
        {/* The Tests list's "generated" badge, worded for criteria (task 1186). */}
        {criterion.source === 'generated' && (
          <span className="px-1.5 py-0.5 rounded-full font-medium bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200">
            suggested
          </span>
        )}
      </span>
      <button type="button" onClick={onEdit} className="text-indigo-600 dark:text-indigo-400 shrink-0">Edit</button>
      <button type="button" onClick={onRemove} className="text-gray-500 hover:text-red-600 shrink-0">Remove</button>
    </li>
  );
}

/**
 * Persisted criteria for one test or for the service (task 1180), edited in
 * place through the criteria routes.
 *
 * Renders nothing with `innovate.fe.testing.criteria` off (task 1215) — the
 * criteria feature set's lever — and then fetches nothing either.
 */
export function CriteriaEditor(props: { scope: CriteriaScope; emptyText?: string }) {
  const criteriaEnabled = useFlag('innovate.fe.testing.criteria');
  return criteriaEnabled ? <CriteriaEditorContent {...props} /> : null;
}

function CriteriaEditorContent({ scope, emptyText }: { scope: CriteriaScope; emptyText?: string }) {
  const { data: criteria = [], isLoading } = useCriteria(scope);
  const createCriterion = useCreateCriterion(scope);
  const updateCriterion = useUpdateCriterion(scope);
  const deleteCriterion = useDeleteCriterion(scope);
  const toast = useToast();
  const [editingId, setEditingId] = useState<string | 'new' | null>(null);

  const run = async (action: () => Promise<unknown>, failure: string) => {
    try {
      await action();
      setEditingId(null);
    } catch (err) {
      toast.error(failure, err instanceof Error ? err.message : undefined);
    }
  };

  return (
    <div className="space-y-1.5" data-testid="criteria-editor">
      {isLoading ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">Loading criteria…</p>
      ) : criteria.length === 0 && editingId !== 'new' ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">{emptyText ?? 'No criteria yet.'}</p>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-gray-700">
          {criteria.map((criterion) =>
            editingId === criterion.id ? (
              <li key={criterion.id} className="py-1.5">
                <CriterionForm
                  initial={criterion}
                  kindLocked
                  saving={updateCriterion.isPending}
                  onCancel={() => setEditingId(null)}
                  onSave={({ name, definition, required }) =>
                    // PATCH cannot change `kind` (shipped contract), so it is not sent.
                    run(
                      () => updateCriterion.mutateAsync({ id: criterion.id, data: { name, definition, required } }),
                      'Failed to save criterion',
                    )
                  }
                />
              </li>
            ) : (
              <CriterionRow
                key={criterion.id}
                testId={`criterion-${criterion.id}`}
                criterion={criterion}
                onEdit={() => setEditingId(criterion.id)}
                onRemove={() => void run(() => deleteCriterion.mutateAsync(criterion.id), 'Failed to remove criterion')}
              />
            ),
          )}
        </ul>
      )}
      {editingId === 'new' ? (
        <CriterionForm
          saving={createCriterion.isPending}
          onCancel={() => setEditingId(null)}
          onSave={(data) => run(() => createCriterion.mutateAsync(data), 'Failed to add criterion')}
        />
      ) : (
        <button type="button" onClick={() => setEditingId('new')} className="inline-flex items-center text-xs font-medium text-indigo-600 dark:text-indigo-400">
          <PlusIcon className="h-3.5 w-3.5 mr-1" />
          Add criterion
        </button>
      )}
    </div>
  );
}

/**
 * Draft criteria for a test that does not exist yet (the Add test form): kept
 * locally and created once the test is.
 */
export function CriteriaDraftEditor({
  drafts,
  onChange,
}: {
  drafts: CreateEndpointTestCriterionInput[];
  onChange: (drafts: CreateEndpointTestCriterionInput[]) => void;
}) {
  const [editingIndex, setEditingIndex] = useState<number | 'new' | null>(null);
  return (
    <div className="space-y-1.5" data-testid="criteria-draft-editor">
      {drafts.length > 0 && (
        <ul className="divide-y divide-gray-100 dark:divide-gray-700">
          {drafts.map((draft, index) =>
            editingIndex === index ? (
              <li key={index} className="py-1.5">
                <CriterionForm
                  initial={draft}
                  onCancel={() => setEditingIndex(null)}
                  onSave={(data) => {
                    onChange(drafts.map((existing, i) => (i === index ? data : existing)));
                    setEditingIndex(null);
                  }}
                />
              </li>
            ) : (
              <CriterionRow
                key={index}
                testId={`criterion-draft-${index}`}
                criterion={draft}
                onEdit={() => setEditingIndex(index)}
                onRemove={() => onChange(drafts.filter((_, i) => i !== index))}
              />
            ),
          )}
        </ul>
      )}
      {editingIndex === 'new' ? (
        <CriterionForm
          onCancel={() => setEditingIndex(null)}
          onSave={(data) => {
            onChange([...drafts, data]);
            setEditingIndex(null);
          }}
        />
      ) : (
        <button type="button" onClick={() => setEditingIndex('new')} className="inline-flex items-center text-xs font-medium text-indigo-600 dark:text-indigo-400">
          <PlusIcon className="h-3.5 w-3.5 mr-1" />
          Add criterion
        </button>
      )}
    </div>
  );
}

export default CriteriaEditor;
